import { useFeatureFlag } from '@/context/FeatureToggleContext/FeatureToggleContext';
import FiltersContext, { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { AdditionalChannelTabs, Channel } from '@/models';
import { FilterType } from '@/models/filter/FilterType';
import { useLocalOptionPaths } from '@/models/filter/utils/localOptionPathsStore';
import { Period } from '@/types/date';
import { isPeriod } from '@/utils/date/isPeriod';
import { LogicalAndFilter, TimeDimension, TimeDimensionComparison } from '@cubejs-client/core';
import { useContext, useMemo } from 'react';
import { formatDateTimeEndOfDayDropTZ, formatDateTimeStartOfDayDropTZ } from '../date';
import { buildAdditionalChannelFilters } from './buildAdditionalChannelFilters';

export interface AdditionalFilterState {
  oneToOne?: string[];
  promo?: string[];
  productMessageGroup?: string[];
  productMessageName?: string[];
  eventType?: string[];
  eventName?: string[];
  interactionType?: string[];
  products?: string[];
  indications?: string[];
  interactionIndication?: string[];
  emailCampaign?: string[];
  emailChannel?: string[];
  emailTopic?: string[];
  websitePortal?: string[];
  fieldForce?: string[];
  type?: string[];
  interactionTypeAllChannels?: string[];
  productLocal?: string[];
  rocheEmployee?: string[];
  localSource?: string[];
}

const DATE_RANGE_FORMAT = 'YYYY-MM-DD';

const useGetDateRangeTimeDimensionComparison = (dimension: string): TimeDimensionComparison => {
  const {
    filterState: { dateRange, comparisonPeriod },
  } = useContext(FiltersContext);

  const { beginn: beginnCurrent, end: endCurrent } = dateRange as Period;
  const { beginn: beginnPrevious, end: endPrevious } = comparisonPeriod as Period;

  return {
    dimension,
    compareDateRange: [
      [formatDateTimeStartOfDayDropTZ(beginnCurrent), formatDateTimeEndOfDayDropTZ(endCurrent)],
      [formatDateTimeStartOfDayDropTZ(beginnPrevious), formatDateTimeEndOfDayDropTZ(endPrevious)],
    ],
  };
};

const useGetDateRangeTimeDimension = (dimension: string): TimeDimension => {
  const {
    filterState: { dateRange },
  } = useFiltersContext();
  const dateRangeStart = isPeriod(dateRange) ? dateRange.beginn.format(DATE_RANGE_FORMAT) : undefined;
  const dateRangeEnd = isPeriod(dateRange) ? dateRange.end.format(DATE_RANGE_FORMAT) : undefined;

  return useMemo<TimeDimension>(() => {
    if (dateRangeStart === undefined || dateRangeEnd === undefined) {
      return { dimension };
    }

    return {
      dimension,
      dateRange: [dateRangeStart, dateRangeEnd],
    };
  }, [dateRangeEnd, dateRangeStart, dimension]);
};

const useGetAdditionalFilter = (
  state: AdditionalFilterState,
  channel?: Channel | AdditionalChannelTabs,
): LogicalAndFilter => {
  if (!channel) channel = Channel.VISITS_1_TO_1;
  const indicationOptionPaths = useLocalOptionPaths(FilterType.INTERACTION_INDICATION);
  const isEmailJourneyInsightsEnabled = useFeatureFlag('emailJourneyInsights');
  return useMemo(
    () => buildAdditionalChannelFilters(state, channel, indicationOptionPaths, isEmailJourneyInsightsEnabled),
    [channel, state, indicationOptionPaths, isEmailJourneyInsightsEnabled],
  );
};

export { useGetAdditionalFilter, useGetDateRangeTimeDimension, useGetDateRangeTimeDimensionComparison };
